import * as React from "react"
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type ColumnFiltersState,
    type ColumnSizingState,
    type ColumnPinningState,
    type ColumnOrderState,
    type ExpandedState,
    type PaginationState,
    type RowSelectionState,
    type SortingState,
    type VisibilityState,
    type OnChangeFn,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../table"
import { DataGridPagination } from "./data-grid-pagination"
import { DataGridToolbar } from "./data-grid-toolbar"
import { DataGridFloatingBar, type FloatingBarAction } from "./data-grid-floating-bar"
import type { RowDensity } from "./data-grid-density-toggle"
import { getCommonPinningStyles } from "./data-grid-utils"
import { Checkbox } from "../checkbox"
import { Spinner } from "../spinner"
import { cn } from "../../../utils"

// Density class mappings
const densityClasses: Record<RowDensity, string> = {
    compact: "py-1 text-xs",
    normal: "py-2",
    comfortable: "py-4",
}

interface DataGridProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    showToolbar?: boolean
    showPagination?: boolean
    // Server-side pagination props
    manualPagination?: boolean
    pageCount?: number
    pagination?: PaginationState
    onPaginationChange?: OnChangeFn<PaginationState>
    // Extended pagination props for manual pagination
    currentPage?: number
    defaultPageSize?: number
    pageSizeOptions?: number[]
    onPageChange?: (page: number) => void
    onPageSizeChange?: (pageSize: number) => void
    totalCount?: number
    // Row selection props
    rowSelection?: RowSelectionState
    onRowSelectionChange?: OnChangeFn<RowSelectionState>
    enableRowSelection?: boolean
    // Select All Across Pages Props (for server-side pagination)
    isAllAcrossPagesSelected?: boolean
    selectAllLoading?: boolean
    onSelectAllAcrossPages?: () => Promise<void> | void
    onDeselectAllAcrossPages?: () => void
    totalSelectableCount?: number
    // Search props
    searchColumn?: string
    searchPlaceholder?: string
    // Loading state
    loading?: boolean
    // Row click handler
    onRowClick?: (row: TData) => void
    // Get row ID for selection
    getRowId?: (row: TData) => string
    // Floating bar actions (bulk actions)
    floatingBarActions?: FloatingBarAction[]
    // Show floating bar
    showFloatingBar?: boolean
    // Column resizing
    enableColumnResizing?: boolean
    // Row density
    rowDensity?: RowDensity
    // Column visibility
    columnVisibility?: VisibilityState
    onColumnVisibilityChange?: OnChangeFn<VisibilityState>
    // Column filters
    columnFilters?: ColumnFiltersState
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
    // Server-side filtering (disables client-side filtering when true)
    manualFiltering?: boolean
    // Sorting
    sorting?: SortingState
    onSortingChange?: OnChangeFn<SortingState>
    enableSorting?: boolean
    manualSorting?: boolean
    // Column pinning
    columnPinning?: ColumnPinningState
    onColumnPinningChange?: OnChangeFn<ColumnPinningState>
    // Column sizing (controlled)
    columnSizing?: ColumnSizingState
    onColumnSizingChange?: OnChangeFn<ColumnSizingState>
    // Column order (controlled)
    columnOrder?: ColumnOrderState
    onColumnOrderChange?: OnChangeFn<ColumnOrderState>
    // Toolbar content slot for custom buttons/controls
    toolbarContent?: React.ReactNode | ((table: import("@tanstack/react-table").Table<TData>) => React.ReactNode)
    // Additional CSS classes for the toolbar's inner content container (e.g. "flex-wrap" for tables with many filters)
    toolbarContentClassName?: string
    // Row reordering (drag-drop)
    enableRowReordering?: boolean
    onRowReorder?: (reorderedData: TData[]) => void
    // Nested rows (expandable)
    enableExpanding?: boolean
    getSubRows?: (row: TData) => TData[] | undefined
    expanded?: ExpandedState
    onExpandedChange?: OnChangeFn<ExpandedState>
    // Master-detail / expanded row content
    renderSubComponent?: (props: { row: import("@tanstack/react-table").Row<TData> }) => React.ReactNode
    getRowCanExpand?: (row: import("@tanstack/react-table").Row<TData>) => boolean
    // Custom empty state
    emptyState?: React.ReactNode
    // Full height mode
    fullHeight?: boolean
    // Disable auto-reset of page index when data changes (default: undefined = TanStack default of true)
    autoResetPageIndex?: boolean
}

export interface DataGridRef<TData> {
    table: import("@tanstack/react-table").Table<TData>
}

// ==================== Select All Across Pages Context ====================

// Context interface for select-all state
export interface SelectAllContextValue {
    isAllAcrossPagesSelected: boolean
    selectAllLoading: boolean
    onSelectAllAcrossPages?: () => Promise<void> | void
    onDeselectAllAcrossPages?: () => void
    totalSelectableCount?: number
    currentPageSelectedCount: number
    currentPageRowCount: number
}

// Context for passing select-all state to column definitions
const SelectAllContext = React.createContext<SelectAllContextValue | null>(null)

// Hook for column definitions to access select-all state
export function useSelectAllContext() {
    return React.useContext(SelectAllContext)
}

// Checkbox component for header that supports select-all-across-pages
export function SelectAllHeaderCheckbox({ table }: { table: import("@tanstack/react-table").Table<any> }) {
    const selectAllCtx = useSelectAllContext()

    // If select-all-across-pages is enabled (context exists and has the callbacks)
    if (selectAllCtx?.onSelectAllAcrossPages) {
        const {
            isAllAcrossPagesSelected,
            selectAllLoading,
            onSelectAllAcrossPages,
            onDeselectAllAcrossPages,
            currentPageSelectedCount,
            currentPageRowCount,
        } = selectAllCtx

        const isChecked = isAllAcrossPagesSelected ||
            (currentPageSelectedCount > 0 && currentPageSelectedCount === currentPageRowCount)
        const isIndeterminate = !isAllAcrossPagesSelected &&
            currentPageSelectedCount > 0 &&
            currentPageSelectedCount < currentPageRowCount

        const handleChange = () => {
            if (selectAllLoading) return

            if (isChecked || isAllAcrossPagesSelected) {
                onDeselectAllAcrossPages?.()
            } else {
                onSelectAllAcrossPages?.()
            }
        }

        if (selectAllLoading) {
            return (
                <div className="flex items-center justify-center w-5 h-5">
                    <Spinner size="sm" />
                </div>
            )
        }

        return (
            <Checkbox
                checked={isIndeterminate ? 'indeterminate' : isChecked}
                onCheckedChange={handleChange}
                aria-label="Select all"
            />
        )
    }

    // Fallback: select/deselect only the current page's rows
    const isAllSelected = table.getIsAllPageRowsSelected()
    const isSomeSelected = table.getIsSomePageRowsSelected()

    return (
        <Checkbox
            checked={isSomeSelected && !isAllSelected ? 'indeterminate' : isAllSelected}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
        />
    )
}

// Row checkbox component that respects select-all-across-pages mode
export function SelectAllRowCheckbox({ row, table }: { row: import("@tanstack/react-table").Row<any>, table: import("@tanstack/react-table").Table<any> }) {
    const selectAllCtx = useSelectAllContext()

    // If select-all-across-pages is active, show as checked
    const isCheckedBySelectAll = selectAllCtx?.isAllAcrossPagesSelected || false
    const isActuallySelected = row.getIsSelected()
    const isChecked = isCheckedBySelectAll || isActuallySelected

    const handleChange = (checked: boolean | 'indeterminate') => {
        // When in select-all mode and user wants to deselect
        if (isCheckedBySelectAll && !checked) {
            // Directly update the selection state to ensure onRowSelectionChange is triggered
            // The parent's handler will detect this deselection and exit select-all mode
            table.setRowSelection((old) => {
                const newSelection = { ...old }
                // Remove this row from selection
                delete newSelection[row.id]
                return newSelection
            })
        } else {
            // Normal toggle
            row.toggleSelected(!!checked)
        }
    }

    return (
        <Checkbox
            checked={isChecked}
            disabled={!row.getCanSelect()}
            onCheckedChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
        />
    )
}

// Helper function to create a select column with proper select-all support
export function createSelectColumn<TData>(): ColumnDef<TData, any> {
    return {
        id: 'select',
        header: ({ table }) => <SelectAllHeaderCheckbox table={table} />,
        cell: ({ row, table }) => <SelectAllRowCheckbox row={row} table={table} />,
        enableSorting: false,
        enableHiding: false,
        size: 40,
    }
}

// ==================== End Select All Across Pages ====================

export const DataGrid = React.forwardRef(<TData, TValue>(
    {
        columns,
        data,
        showToolbar = true,
        showPagination = true,
        manualPagination = false,
        pageCount,
        pagination: externalPagination,
        onPaginationChange: externalOnPaginationChange,
        // Extended pagination props
        currentPage,
        defaultPageSize = 10,
        pageSizeOptions: _pageSizeOptions,
        onPageChange,
        onPageSizeChange,
        totalCount: _totalCount,
        rowSelection: externalRowSelection,
        onRowSelectionChange: externalOnRowSelectionChange,
        enableRowSelection = true,
        // Select All Across Pages
        isAllAcrossPagesSelected = false,
        selectAllLoading = false,
        onSelectAllAcrossPages,
        onDeselectAllAcrossPages,
        totalSelectableCount,
        searchColumn = "email",
        searchPlaceholder = "Filter...",
        loading = false,
        onRowClick,
        getRowId,
        floatingBarActions,
        showFloatingBar = true,
        enableColumnResizing = false,
        rowDensity = "normal",
        // New controlled props
        columnVisibility: externalColumnVisibility,
        onColumnVisibilityChange: externalOnColumnVisibilityChange,
        columnFilters: externalColumnFilters,
        onColumnFiltersChange: externalOnColumnFiltersChange,
        manualFiltering = false,
        sorting: externalSorting,
        onSortingChange: externalOnSortingChange,
        enableSorting: _enableSorting,
        manualSorting: _manualSorting,
        columnPinning: externalColumnPinning,
        onColumnPinningChange: externalOnColumnPinningChange,
        columnSizing: externalColumnSizing,
        onColumnSizingChange: externalOnColumnSizingChange,
        columnOrder: externalColumnOrder,
        onColumnOrderChange: externalOnColumnOrderChange,
        toolbarContent,
        toolbarContentClassName,
        // Row reordering
        enableRowReordering = false,
        onRowReorder,
        // Nested rows (expandable)
        enableExpanding = false,
        getSubRows,
        expanded: externalExpanded,
        onExpandedChange: externalOnExpandedChange,
        renderSubComponent,
        getRowCanExpand,
        emptyState,
        fullHeight = false,
        autoResetPageIndex,
    }: DataGridProps<TData, TValue>,
    ref: React.Ref<DataGridRef<TData>>
) => {
    // Internal state for client-side mode
    const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({})
    const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
        pageIndex: currentPage !== undefined ? currentPage - 1 : 0,
        pageSize: defaultPageSize,
    })
    const [internalColumnVisibility, setInternalColumnVisibility] = React.useState<VisibilityState>({})
    const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([])
    const [internalSorting, setInternalSorting] = React.useState<SortingState>([])
    const [internalColumnSizing, setInternalColumnSizing] = React.useState<ColumnSizingState>({})
    const [internalColumnPinning, setInternalColumnPinning] = React.useState<ColumnPinningState>({})
    const [internalColumnOrder, setInternalColumnOrder] = React.useState<ColumnOrderState>([])

    // Row reordering drag state
    const [draggedRowId, setDraggedRowId] = React.useState<string | null>(null)
    const [dragOverRowId, setDragOverRowId] = React.useState<string | null>(null)

    // Expanded state for nested rows
    const [internalExpanded, setInternalExpanded] = React.useState<ExpandedState>({})

    // Sync pagination when currentPage prop changes
    React.useEffect(() => {
        if (currentPage !== undefined) {
            setInternalPagination(prev => ({
                ...prev,
                pageIndex: currentPage - 1,
            }))
        }
    }, [currentPage])

    // Sync pageSize when defaultPageSize prop changes (e.g. restored from preferences)
    React.useEffect(() => {
        setInternalPagination(prev => {
            if (prev.pageSize === defaultPageSize) return prev
            return { ...prev, pageSize: defaultPageSize }
        })
    }, [defaultPageSize])

    // Use external state if provided, otherwise use internal
    const rowSelection = externalRowSelection ?? internalRowSelection
    const onRowSelectionChange = externalOnRowSelectionChange ?? setInternalRowSelection
    const pagination = externalPagination ?? internalPagination

    // Wrap pagination change to call convenience callbacks
    const onPaginationChange = React.useCallback<OnChangeFn<PaginationState>>((updaterOrValue) => {
        const newValue = typeof updaterOrValue === 'function'
            ? updaterOrValue(pagination)
            : updaterOrValue

        // Call the external handler if provided
        if (externalOnPaginationChange) {
            externalOnPaginationChange(updaterOrValue)
        } else {
            setInternalPagination(updaterOrValue)
        }

        // Call convenience callbacks if page or pageSize changed
        if (newValue.pageIndex !== pagination.pageIndex && onPageChange) {
            onPageChange(newValue.pageIndex + 1)
        }
        if (newValue.pageSize !== pagination.pageSize && onPageSizeChange) {
            onPageSizeChange(newValue.pageSize)
        }
    }, [pagination, externalOnPaginationChange, onPageChange, onPageSizeChange])
    // Managed state
    const columnVisibility = externalColumnVisibility ?? internalColumnVisibility
    const onColumnVisibilityChange = externalOnColumnVisibilityChange ?? setInternalColumnVisibility
    const columnFilters = externalColumnFilters ?? internalColumnFilters
    const onColumnFiltersChange = externalOnColumnFiltersChange ?? setInternalColumnFilters
    const sorting = externalSorting ?? internalSorting
    const onSortingChange = externalOnSortingChange ?? setInternalSorting
    const columnSizing = externalColumnSizing ?? internalColumnSizing
    const onColumnSizingChange = externalOnColumnSizingChange ?? setInternalColumnSizing
    const columnPinning = externalColumnPinning ?? internalColumnPinning
    const onColumnPinningChange = externalOnColumnPinningChange ?? setInternalColumnPinning
    const columnOrder = externalColumnOrder ?? internalColumnOrder
    const onColumnOrderChange = externalOnColumnOrderChange ?? setInternalColumnOrder
    const expanded = externalExpanded ?? internalExpanded
    const onExpandedChange = externalOnExpandedChange ?? setInternalExpanded

    const table = useReactTable({
        data,
        columns,
        defaultColumn: {
            minSize: 50,
            maxSize: 1000,
            size: 150,
            enableResizing: true,
        },
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing,
            columnPinning,
            columnOrder,
            expanded,
        },
        getRowId,
        enableRowSelection,
        onRowSelectionChange,
        onSortingChange,
        onColumnFiltersChange,
        onColumnVisibilityChange,
        onPaginationChange,
        manualPagination,
        manualFiltering,
        autoResetPageIndex,
        enableColumnResizing,
        columnResizeMode: "onChange" as const,
        onColumnSizingChange,
        onColumnPinningChange,
        onColumnOrderChange,
        pageCount: pageCount ?? -1,
        rowCount: _totalCount ?? totalSelectableCount,
        getCoreRowModel: getCoreRowModel(),
        // Disable client-side filtering when manualFiltering is true (server-side filtering)
        getFilteredRowModel: (manualFiltering || manualPagination) ? undefined : getFilteredRowModel(),
        getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
        getSortedRowModel: manualPagination ? undefined : getSortedRowModel(),
        // Keep faceted models for filter UI even with manual filtering
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        // Nested rows
        getSubRows: enableExpanding ? getSubRows : undefined,
        getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
        onExpandedChange: enableExpanding ? onExpandedChange : undefined,
        getRowCanExpand: enableExpanding && getRowCanExpand ? getRowCanExpand : undefined,
    })

    // Note: page-level selection methods are intentionally NOT overridden here.
    // toggleAllPageRowsSelected selects only the current page's rows, which is
    // the correct behaviour. Use SelectAllContext with onSelectAllAcrossPages
    // for explicit cross-page selection when needed.

    React.useImperativeHandle(ref, () => ({
        table,
    }))

    // Row reordering handlers
    const handleDragStart = React.useCallback((e: React.DragEvent, rowId: string) => {
        setDraggedRowId(rowId)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', rowId)
        // Set drag image to the row
        if (e.currentTarget && typeof e.dataTransfer.setDragImage === 'function') {
            const row = e.currentTarget as HTMLElement
            // e.dataTransfer.setDragImage(row, 0, 0) // Optional: explicit drag image
        }
    }, [])

    const handleDragOver = React.useCallback((e: React.DragEvent, rowId: string) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (draggedRowId !== rowId) {
            setDragOverRowId(rowId)
        }
    }, [draggedRowId])

    const handleDrop = React.useCallback((e: React.DragEvent, targetRowId: string) => {
        e.preventDefault()
        if (!draggedRowId || draggedRowId === targetRowId || !onRowReorder) {
            setDraggedRowId(null)
            setDragOverRowId(null)
            return
        }

        const draggedIndex = data.findIndex((item, idx) => {
            const id = getRowId ? getRowId(item) : String(idx)
            return id === draggedRowId
        })
        const targetIndex = data.findIndex((item, idx) => {
            const id = getRowId ? getRowId(item) : String(idx)
            return id === targetRowId
        })

        if (draggedIndex === -1 || targetIndex === -1) {
            setDraggedRowId(null)
            setDragOverRowId(null)
            return
        }

        const newData = [...data]
        const [draggedItem] = newData.splice(draggedIndex, 1)
        newData.splice(targetIndex, 0, draggedItem)

        onRowReorder(newData)
        setDraggedRowId(null)
        setDragOverRowId(null)
    }, [draggedRowId, data, getRowId, onRowReorder])

    const handleDragEnd = React.useCallback(() => {
        setDraggedRowId(null)
        setDragOverRowId(null)
    }, [])

    const handleDragLeave = React.useCallback(() => {
        setDragOverRowId(null)
    }, [])

    // Compute select-all context value for the provider
    const selectAllContextValue = React.useMemo<SelectAllContextValue | null>(() => {
        // Only provide context if select-all-across-pages is enabled
        if (!onSelectAllAcrossPages) return null

        // Count only the current page's selected rows, not all selections
        const currentPageSelectedCount = data.filter((row, index) => {
            const rowId = getRowId ? getRowId(row) : String(index)
            return rowSelection[rowId]
        }).length

        return {
            isAllAcrossPagesSelected,
            selectAllLoading,
            onSelectAllAcrossPages,
            onDeselectAllAcrossPages,
            totalSelectableCount,
            currentPageSelectedCount,
            currentPageRowCount: data.length,
        }
    }, [
        isAllAcrossPagesSelected,
        selectAllLoading,
        onSelectAllAcrossPages,
        onDeselectAllAcrossPages,
        totalSelectableCount,
        rowSelection,
        data,
        getRowId,
    ])

    return (
        <SelectAllContext.Provider value={selectAllContextValue}>
            <div className={fullHeight ? "flex flex-col h-full space-y-4" : "space-y-4"}>
                <div className={fullHeight ? "flex-1 flex flex-col rounded-md border overflow-hidden" : "rounded-md border overflow-hidden"}>
                    {showToolbar && (
                        <div className="bg-background flex-none">
                            <DataGridToolbar
                                table={table}
                                searchColumn={searchColumn}
                                searchPlaceholder={searchPlaceholder}
                                columnOrder={columnOrder}
                                onColumnOrderChange={onColumnOrderChange}
                                contentClassName={toolbarContentClassName}
                            >
                                {typeof toolbarContent === 'function' ? toolbarContent(table) : toolbarContent}
                            </DataGridToolbar>
                        </div>
                    )}
                    <div className={fullHeight ? "flex-1 overflow-auto" : "overflow-auto"}>
                        <Table className="table-fixed" style={{ minWidth: table.getTotalSize() }}>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    style={{
                                                        ...getCommonPinningStyles({ column: header.column, isHeader: true }),
                                                        width: header.getSize(),
                                                    }}
                                                    className="relative group overflow-hidden"
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            )
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center"
                                        >
                                            <div className="flex justify-center p-4">
                                                <Spinner size="lg" />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <TableRow
                                                data-state={row.getIsSelected() && "selected"}
                                                onClick={() => onRowClick?.(row.original)}
                                                className={`
                                            ${onRowClick ? "cursor-pointer" : ""}
                                            ${enableRowReordering ? "cursor-move" : ""}
                                            ${draggedRowId === row.id ? "opacity-50 scale-95" : ""}
                                            ${dragOverRowId === row.id && draggedRowId !== row.id ? "border-t-2 border-blue-400 bg-blue-50" : ""}
                                            border-b last:border-b-0
                                        `.trim()}
                                            draggable={enableRowReordering}
                                            onDragStart={enableRowReordering ? (e) => handleDragStart(e, row.id) : undefined}
                                            onDragOver={enableRowReordering ? (e) => handleDragOver(e, row.id) : undefined}
                                            onDrop={enableRowReordering ? (e) => handleDrop(e, row.id) : undefined}
                                            onDragEnd={enableRowReordering ? handleDragEnd : undefined}
                                            onDragLeave={enableRowReordering ? handleDragLeave : undefined}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    style={{
                                                        ...getCommonPinningStyles({ column: cell.column }),
                                                    }}
                                                    className={cn(
                                                        "overflow-hidden",
                                                        densityClasses[rowDensity],
                                                        (cell.column.columnDef.meta as { cellClassName?: string } | undefined)?.cellClassName
                                                    )}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {enableExpanding && row.getIsExpanded() && renderSubComponent && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={columns.length}
                                                    className="p-0"
                                                >
                                                    {renderSubComponent({ row })}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-80"
                                    >
                                        {emptyState ? (
                                            emptyState
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12">
                                                <img
                                                    src="/assets/EmptyTable1.png"
                                                    alt="No data"
                                                    className="w-40 h-40 mb-4 opacity-80"
                                                />
                                                <p className="text-gray-500 text-sm">No results.</p>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                {showPagination && (
                    <div className="py-2 flex-none mt-auto border-t border-gray-100 bg-white">
                        <DataGridPagination
                            table={table}
                            manualPagination={manualPagination}
                            isAllAcrossPagesSelected={isAllAcrossPagesSelected}
                            totalSelectableCount={totalSelectableCount}
                            actualSelectedCount={Object.keys(rowSelection).filter(k => rowSelection[k]).length}
                        />
                    </div>
                )}
            </div>
            {showFloatingBar && floatingBarActions && (
                <DataGridFloatingBar
                    table={table}
                    actions={floatingBarActions}
                />
            )}
        </div>
        </SelectAllContext.Provider>
    )
}) as <TData, TValue>(
    props: DataGridProps<TData, TValue> & { ref?: React.Ref<DataGridRef<TData>> }
) => React.ReactElement

// Re-export types for convenience
export type { ColumnDef, PaginationState, RowSelectionState, SortingState, VisibilityState, ColumnFiltersState, ColumnPinningState, ColumnSizingState, ColumnOrderState, ExpandedState } from "@tanstack/react-table"
export type { FloatingBarAction } from "./data-grid-floating-bar"
export { DataGridFacetedFilter } from "./data-grid-faceted-filter"
export { DataGridColumnHeader } from "./data-grid-column-header"
export { DataGridViewOptions } from "./data-grid-view-options"
export { DataGridDensityToggle, type RowDensity } from "./data-grid-density-toggle"
export { DataGridToolbar } from "./data-grid-toolbar"
export { DataGridToolbarFilter, getFilterableColumns, type FilterColumnMeta } from "./data-grid-toolbar-filter"
export { DataGridFilterMenu } from "./data-grid-filter-menu"
export { DataGridCommandFilter, type CommandFilterOption, type FetchOptionsCallback } from "./data-grid-command-filter"
export { EditableCell } from "./editable-cell"
export { EditableTextCell, EditableSelectCell, EditableDateCell } from "./editable-cell-variants"
export { DataGridDragHandle } from "./data-grid-drag-handle"
export { DataGridExpandToggle } from "./data-grid-expand-toggle"
// Note: SelectAllHeaderCheckbox, createSelectColumn, useSelectAllContext, and SelectAllContextValue
// are already exported inline above (lines ~135-225)